import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import { CustomImage } from '../../lib/tiptapExtensions';
import InsertImageModal from '../InsertImageModal';
import { HTMLViewModal } from '../HTMLViewModal';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link as LinkIcon,
  Heading1,
  Heading2,
  Highlighter,
  ImageIcon,
  Undo,
  Redo,
  X,
  Code,
} from 'lucide-react';

interface TemplateEditorModalProps {
  template: {
    id: string;
    template_type: string;
    subject: string;
    html_body: string | null;
    body: string;
  };
  onClose: () => void;
  onSave: (templateId: string, subject: string, htmlBody: string) => Promise<void>;
}

export function TemplateEditorModal({ template, onClose, onSave }: TemplateEditorModalProps) {
  const [subject, setSubject] = useState(template.subject);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [showHtmlModal, setShowHtmlModal] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Highlight,
      CustomImage.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: {
          class: 'max-w-full h-auto',
        },
      }),
    ],
    content: template.html_body || template.body,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[400px] p-4',
      },
    },
  });

  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  const handleSave = async () => {
    if (!editor) return;

    setError('');
    setSaving(true);

    try {
      const htmlContent = editor.getHTML();
      await onSave(template.id, subject, htmlContent);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const setLink = () => {
    if (!editor) return;

    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL:', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const addImage = () => {
    setShowImageModal(true);
  };

  const handleImageInsert = (data: { src: string; width: string; height: string; alt: string }) => {
    if (!editor) return;

    const widthNum = parseInt(data.width, 10);
    const heightNum = parseInt(data.height, 10);
    editor.chain().focus().setImage({
      src: data.src,
      alt: data.alt,
      width: Number.isFinite(widthNum) ? widthNum : undefined,
      height: Number.isFinite(heightNum) ? heightNum : undefined,
    }).run();
  };

  const handleHtmlApply = (html: string) => {
    if (!editor) return;
    editor.commands.setContent(html);
  };

  if (!editor) {
    return null;
  }

  return (
    <>
      {showImageModal && (
        <InsertImageModal
          onInsert={handleImageInsert}
          onClose={() => setShowImageModal(false)}
        />
      )}
      {showHtmlModal && editor && (
        <HTMLViewModal
          html={editor.getHTML()}
          onClose={() => setShowHtmlModal(false)}
          onApply={handleHtmlApply}
          title="Template HTML"
        />
      )}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Template Bewerken</h2>
            <p className="text-sm text-gray-600 mt-1">{template.template_type}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Onderwerp
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Email onderwerp"
              />
              <p className="text-xs text-gray-500 mt-1">
                Je kunt variabelen gebruiken: &#123;&#123;first_name&#125;&#125;, &#123;&#123;company_name&#125;&#125;, etc.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Body
              </label>

              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <div className="bg-gray-50 border-b border-gray-300 p-2 flex flex-wrap gap-1">
                  <button
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                      editor.isActive('bold') ? 'bg-gray-300' : ''
                    }`}
                    title="Bold"
                  >
                    <Bold className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                      editor.isActive('italic') ? 'bg-gray-300' : ''
                    }`}
                    title="Italic"
                  >
                    <Italic className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                      editor.isActive('underline') ? 'bg-gray-300' : ''
                    }`}
                    title="Underline"
                  >
                    <UnderlineIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => editor.chain().focus().toggleHighlight().run()}
                    className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                      editor.isActive('highlight') ? 'bg-gray-300' : ''
                    }`}
                    title="Highlight"
                  >
                    <Highlighter className="w-4 h-4" />
                  </button>

                  <div className="w-px bg-gray-300 mx-1" />

                  <button
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                      editor.isActive('heading', { level: 1 }) ? 'bg-gray-300' : ''
                    }`}
                    title="Heading 1"
                  >
                    <Heading1 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                      editor.isActive('heading', { level: 2 }) ? 'bg-gray-300' : ''
                    }`}
                    title="Heading 2"
                  >
                    <Heading2 className="w-4 h-4" />
                  </button>

                  <div className="w-px bg-gray-300 mx-1" />

                  <button
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                      editor.isActive('bulletList') ? 'bg-gray-300' : ''
                    }`}
                    title="Bullet List"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                      editor.isActive('orderedList') ? 'bg-gray-300' : ''
                    }`}
                    title="Numbered List"
                  >
                    <ListOrdered className="w-4 h-4" />
                  </button>

                  <div className="w-px bg-gray-300 mx-1" />

                  <button
                    onClick={setLink}
                    className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                      editor.isActive('link') ? 'bg-gray-300' : ''
                    }`}
                    title="Add Link"
                  >
                    <LinkIcon className="w-4 h-4" />
                  </button>

                  <div className="w-px bg-gray-300 mx-1" />

                  <button
                    onClick={addImage}
                    className="p-2 rounded hover:bg-gray-200 transition-colors"
                    title="Add Image"
                  >
                    <ImageIcon className="w-4 h-4" />
                  </button>

                  <div className="w-px bg-gray-300 mx-1" />

                  <button
                    onClick={() => setShowHtmlModal(true)}
                    className="p-2 rounded hover:bg-gray-200 transition-colors bg-blue-50 hover:bg-blue-100"
                    title="HTML Bekijken/Bewerken"
                  >
                    <Code className="w-4 h-4 text-blue-600" />
                  </button>

                  <div className="w-px bg-gray-300 mx-1" />

                  <button
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                    className="p-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-30"
                    title="Undo"
                  >
                    <Undo className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                    className="p-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-30"
                    title="Redo"
                  >
                    <Redo className="w-4 h-4" />
                  </button>
                </div>

                <div className="bg-white">
                  <EditorContent editor={editor} />
                </div>
              </div>

              <p className="text-xs text-gray-500 mt-2">
                Je kunt variabelen typen: &#123;&#123;first_name&#125;&#125;, &#123;&#123;company_name&#125;&#125;, &#123;&#123;magic_link&#125;&#125;, etc.
              </p>
            </div>

            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Opslaan...' : 'Opslaan'}
          </button>
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
          >
            Annuleren
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
